import * as React from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CalendarCheck,
  CircleSlash,
  FileUp,
  FileWarning,
  History,
  PackageCheck,
  PencilLine,
  Send,
  Undo2,
  User,
} from 'lucide-react';

import { api, ErroApi } from '@/lib/api';
import { INTERVALO_ATUALIZACAO_MS } from '@/lib/consultas';
import { formatarData, formatarDataHora } from '@/lib/utils';
import { calcularIdade, mascararCns, mascararCpf, mascararTelefone } from '@/lib/mascaras';
import type { EventoHistorico, SolicitacaoDetalhe } from '@/features/solicitacoes/tipos';
import { ROTULOS_TIPO } from '@/features/solicitacoes/status';
import { StatusBadge } from '@/components/status-badge';
import { BadgePrioridade } from '@/components/badge-prioridade';
import { BadgeOrigem } from '@/components/badge-origem';
import { LinhaDoTempo } from '@/components/linha-do-tempo';
import { IndicadorAtualizacao } from '@/components/indicador-atualizacao';
import { PainelAnexos } from '@/components/painel-anexos';
import { PainelNotificacoes } from '@/components/painel-notificacoes';
import { PainelDecisao } from '@/components/regulacao/painel-decisao';
import { PainelCancelamento } from '@/components/cancelamento/painel-cancelamento';
import { BotaoHistoricoPaciente } from '@/components/historico-paciente/botao-historico-paciente';
import { BotaoImprimirMarcacao } from '@/components/marcacao/botao-imprimir-marcacao';
import { podeEditar, podeEditarPerfil } from '@/features/solicitacoes/edicao';
import { caminhoDaLista } from '@/features/solicitacoes/origem';
import { ehResponsavelDaUbs } from '@/features/solicitacoes/responsaveis';
import { useAuth } from '@/features/auth/contexto';
import { Botao } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import { Cartao, CartaoCabecalho, CartaoConteudo, CartaoTitulo } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/** Estados em que a solicitação está encerrada e não recebe novos documentos. */
const ENCERRADOS = ['AGENDADO', 'DEVOLVIDO', 'CANCELADO'];

export function PaginaDetalheSolicitacao() {
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: solicitacao,
    isPending,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['solicitacao', id],
    queryFn: () => api.get<SolicitacaoDetalhe>(`/solicitacoes/${id}`),
    enabled: Boolean(id),
    // A UBS costuma deixar o protocolo aberto acompanhando; sem isso, uma
    // devolução ou agendamento só apareceria depois de recarregar.
    refetchInterval: INTERVALO_ATUALIZACAO_MS,
  });

  const { data: historico } = useQuery({
    queryKey: ['historico', id],
    queryFn: () => api.get<EventoHistorico[]>(`/solicitacoes/${id}/historico`),
    enabled: Boolean(id),
    refetchInterval: INTERVALO_ATUALIZACAO_MS,
  });

  /**
   * Leitura automática (seção 7 do plano).
   *
   * Disparada ao abrir a tela, mas quem decide se muda algo é o backend: ele
   * só altera quando o status é ENVIADO e quem abre é da Regulação. A resposta
   * diz se houve mudança, e só então vale recarregar.
   */
  const marcarLida = useMutation({
    mutationFn: () =>
      api.post<{
        alterada: boolean;
        documentoNovoBaixado: boolean;
        edicaoNovaBaixada: boolean;
      }>(`/solicitacoes/${id}/marcar-como-lida`),
    onSuccess: async (resultado) => {
      // A baixa dos avisos de documento e de correção não recarrega esta tela
      // de propósito: o recado desapareceria debaixo dos olhos de quem acabou
      // de abri-la. A fila, essa sim, precisa perder o selo.
      if (resultado.documentoNovoBaixado || resultado.edicaoNovaBaixada) {
        await queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
        await queryClient.invalidateQueries({ queryKey: ['estatisticas'] });
      }

      if (!resultado.alterada) return;
      await queryClient.invalidateQueries({ queryKey: ['solicitacao', id] });
      await queryClient.invalidateQueries({ queryKey: ['historico', id] });
      await queryClient.invalidateQueries({ queryKey: ['estatisticas'] });
    },
  });

  const marcar = marcarLida.mutate;
  React.useEffect(() => {
    if (id && usuario?.perfil === 'REGULACAO') marcar();
  }, [id, usuario?.perfil, marcar]);

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-64" />
        <Esqueleto className="h-40 w-full" />
        <Esqueleto className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    const naoEncontrada = error instanceof ErroApi && error.status === 404;

    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <AlertCircle className="text-silenciado-texto size-12" aria-hidden="true" />
        <h1 className="text-lg font-semibold">
          {naoEncontrada ? 'Solicitação não encontrada' : 'Não foi possível carregar'}
        </h1>
        <p className="text-silenciado-texto max-w-prose text-sm">
          {naoEncontrada
            ? 'O protocolo não existe ou pertence a outra unidade.'
            : error instanceof ErroApi
              ? error.mensagemUsuario
              : 'Tente novamente em instantes.'}
        </p>
        <Botao asChild variante="contorno">
          <Link to="/solicitacoes">Voltar à lista</Link>
        </Botao>
      </div>
    );
  }

  const idade = calcularIdade(solicitacao.pacienteDataNascimento);
  const podeAnexar = !ENCERRADOS.includes(solicitacao.status);

  /**
   * Oferta da correção de dados.
   *
   * A Regulação segue a mesma regra das decisões — só os reguladores
   * responsáveis pela unidade —, e é o backend que garante. Aqui a checagem serve para não
   * oferecer um botão que levaria a um formulário impossível de salvar.
   */
  const podeCorrigir =
    usuario !== null &&
    podeEditar(solicitacao.status) &&
    podeEditarPerfil(usuario.perfil, solicitacao.origem) &&
    (usuario.perfil !== 'REGULACAO' || ehResponsavelDaUbs(solicitacao.ubs, usuario.id));

  return (
    <div className="flex flex-col gap-5">
      <div>
        {/* O registro volta para a lista onde mora: o detalhe tem uma URL só,
            mas a demanda reprimida é acompanhada no menu dela. */}
        <Botao variante="fantasma" tamanho="sm" asChild className="-ml-3 mb-2">
          <Link to={caminhoDaLista(solicitacao.origem)}>
            <ArrowLeft aria-hidden="true" />
            Voltar
          </Link>
        </Botao>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">
            {solicitacao.protocolo}
          </h1>
          <StatusBadge status={solicitacao.status} />
          <BadgeOrigem origem={solicitacao.origem} />
          <BadgePrioridade prioridade={solicitacao.prioridade} />

          <div className="ml-auto flex flex-wrap gap-2">
            {(solicitacao.pacienteCpf || solicitacao.pacienteCns) && (
              <BotaoHistoricoPaciente solicitacaoId={solicitacao.id} />
            )}
            {/* Só o que tem identificador do eSUS: a marcação impressa sai de lá. */}
            {solicitacao.status === 'AGENDADO' && solicitacao.idEsusRegulacao && (
              <BotaoImprimirMarcacao solicitacaoId={solicitacao.id} />
            )}
            {podeCorrigir && (
              <Botao variante="contorno" tamanho="sm" asChild>
                <Link to={`/solicitacoes/${solicitacao.id}/editar`}>
                  <PencilLine aria-hidden="true" />
                  Editar dados
                </Link>
              </Botao>
            )}
          </div>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-silenciado-texto text-sm">
            {solicitacao.pedidoOriginalEm
              ? `Pedido original de ${formatarData(solicitacao.pedidoOriginalEm)} · registrado no KAVI em ${formatarDataHora(solicitacao.criadoEm)} por ${solicitacao.criadoPor.nome}`
              : `Registrada em ${formatarDataHora(solicitacao.criadoEm)} por ${solicitacao.criadoPor.nome}`}
          </p>
          <IndicadorAtualizacao buscando={isFetching} />
        </div>
      </div>

      {/* Avisos de decisão da Regulação, com destaque no topo */}
      {solicitacao.status === 'DEVOLVIDO' && solicitacao.motivoDevolucao && (
        <Aviso
          icone={Undo2}
          titulo="Solicitação devolvida"
          tom="bg-status-devolvido-fundo text-status-devolvido-texto border-status-devolvido-borda"
        >
          {solicitacao.motivoDevolucao}
        </Aviso>
      )}

      {solicitacao.status === 'CANCELADO' && (
        <Aviso
          icone={CircleSlash}
          titulo="Solicitação cancelada"
          tom="bg-status-cancelado-fundo text-status-cancelado-texto border-status-cancelado-borda"
        >
          {solicitacao.cancelamentoMotivo ?? 'Cancelada a pedido da UBS.'}
          <p className="mt-2 text-xs">
            Cancelada em {formatarDataHora(solicitacao.canceladoEm)}. Para retomar o processo é
            preciso reabrir a solicitação, o que é feito pela Regulação.
          </p>
        </Aviso>
      )}

      {solicitacao.status === 'PENDENCIA_DOCUMENTACAO' && solicitacao.descricaoPendencia && (
        <Aviso
          icone={FileWarning}
          titulo="Pendência de documentação"
          tom="bg-status-pendencia-fundo text-status-pendencia-texto border-status-pendencia-borda"
        >
          {solicitacao.descricaoPendencia}
          <p className="mt-2 text-xs">
            Anexe os documentos solicitados no painel de documentos abaixo.
          </p>
        </Aviso>
      )}

      {/* Chega antes dos avisos de decisão: é o que o regulador precisa ver
          primeiro ao abrir um processo que recebeu documento. A marca é
          apagada no servidor nesta mesma abertura. */}
      {solicitacao.documentoNovoEm && usuario?.perfil === 'REGULACAO' && (
        <Aviso
          icone={FileUp}
          titulo="Documento novo anexado pela UBS"
          tom="bg-status-pendencia-fundo text-status-pendencia-texto border-status-pendencia-borda"
        >
          A UBS anexou documento em {formatarDataHora(solicitacao.documentoNovoEm)}. Confira no
          painel de documentos abaixo e reavalie a solicitação — anexar não muda o status sozinho.
        </Aviso>
      )}

      {/* Ao lado do aviso de documento: editar também não muda status, e a
          correção precisa chegar a quem está analisando o pedido. O que mudou
          está no histórico, ao lado. */}
      {solicitacao.edicaoNovaEm && usuario?.perfil === 'REGULACAO' && (
        <Aviso
          icone={PencilLine}
          titulo="Dados do pedido corrigidos"
          tom="bg-status-lido-fundo text-status-lido-texto border-status-lido-borda"
        >
          A solicitação foi corrigida em {formatarDataHora(solicitacao.edicaoNovaEm)}, depois de
          você tê-la lido. Confira o histórico para ver o que mudou — corrigir não altera o status
          sozinho.
        </Aviso>
      )}

      {solicitacao.status === 'EM_PROCESSO' && (
        <Aviso
          icone={PackageCheck}
          titulo="Documentação recebida — em processo"
          tom="bg-status-processo-fundo text-status-processo-texto border-status-processo-borda"
        >
          A Regulação confirmou o recebimento da documentação física e está processando esta
          solicitação.
        </Aviso>
      )}

      {/* Agendamento declarado, e não conferido. A grade de campos não entra
          aqui: local, data e profissional são todos nulos — o atendimento foi
          marcado fora do KAVI —, e mostrá-los vazios pareceria defeito em vez
          de dizer o que de fato se sabe. */}
      {solicitacao.status === 'AGENDADO' && solicitacao.agendamentoOrigem === 'ANTERIOR' && (
        <Aviso
          icone={History}
          titulo="Já agendada antes do KAVI"
          tom="bg-status-agendado-fundo text-status-agendado-texto border-status-agendado-borda"
        >
          <p className="mt-1">
            O atendimento já estava marcado quando esta demanda reprimida foi cadastrada. Os dados
            do agendamento estão fora do KAVI e não passaram pela conferência de identidade; o que
            se sabe está na justificativa de quem declarou.
          </p>
          {solicitacao.agendamentoJustificativa && (
            <p className="mt-2">
              <strong>Justificativa:</strong> {solicitacao.agendamentoJustificativa}
            </p>
          )}
          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Etiqueta tom="alerta">
              <History className="size-3" aria-hidden="true" />
              Sem aviso ao paciente
            </Etiqueta>
            <span>
              Nenhuma notificação foi enviada: o atendimento é anterior ao sistema e pode já ter
              acontecido.
            </span>
          </p>
        </Aviso>
      )}

      {solicitacao.status === 'AGENDADO' && solicitacao.agendamentoOrigem !== 'ANTERIOR' && (
        <Aviso
          icone={CalendarCheck}
          titulo="Agendamento confirmado"
          tom="bg-status-agendado-fundo text-status-agendado-texto border-status-agendado-borda"
        >
          <dl className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-2">
            <ItemAgendamento rotulo="Local" valor={solicitacao.agendamentoLocal} />
            <ItemAgendamento
              rotulo="Data"
              valor={solicitacao.agendamentoData ? formatarData(solicitacao.agendamentoData) : null}
            />
            <ItemAgendamento rotulo="Horário" valor={solicitacao.agendamentoHora} />
            <ItemAgendamento rotulo="Profissional" valor={solicitacao.agendamentoProfissional} />
            {/* O campo guarda o procedimento nos dois caminhos: pelo eSUS vem
                de `noProcedimentoInterno`, e no manual é o item escolhido do
                catálogo. Chamá-lo de "Especialidade" fazia a tela mentir. A
                especialidade de verdade é a da solicitação, ao lado. */}
            <ItemAgendamento rotulo="Procedimento" valor={solicitacao.agendamentoEspecialidade} />
            <ItemAgendamento rotulo="Especialidade" valor={solicitacao.especialidade.nome} />
            <ItemAgendamento rotulo="ID eSUS" valor={solicitacao.idEsusRegulacao} />
            <ItemAgendamento
              rotulo="Situação no eSUS"
              valor={solicitacao.agendamentoSituacaoEsus}
            />
            <ItemAgendamento rotulo="Endereço" valor={solicitacao.agendamentoEnderecoLocal} />
            <ItemAgendamento
              rotulo="Telefone do local"
              valor={solicitacao.agendamentoTelefoneLocal}
            />
          </dl>

          {/* Quem lê este agendamento precisa saber que ele não passou pela
              conferência de identidade — ela depende do retorno do eSUS. */}
          {solicitacao.agendamentoOrigem === 'MANUAL' && (
            <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <Etiqueta tom="alerta">
                <PencilLine className="size-3" aria-hidden="true" />
                Preenchido manualmente
              </Etiqueta>
              <span>
                Dados digitados pela Regulação porque o eSUS não os devolveu
                {solicitacao.agendamentoMotivoManual
                  ? `: ${solicitacao.agendamentoMotivoManual}`
                  : '.'}
              </span>
            </p>
          )}
        </Aviso>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5 lg:col-span-2">
          {/* Antes do painel de decisão: um pedido de cancelamento em aberto
              muda o que faz sentido decidir sobre a solicitação. */}
          <PainelCancelamento solicitacao={solicitacao} />

          <PainelDecisao solicitacao={solicitacao} />

          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo className="flex items-center gap-2">
                <User className="size-4" aria-hidden="true" />
                Paciente
              </CartaoTitulo>
            </CartaoCabecalho>
            <CartaoConteudo>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <Dado rotulo="Nome" valor={solicitacao.pacienteNome} />
                <Dado
                  rotulo="Data de nascimento"
                  valor={`${formatarData(solicitacao.pacienteDataNascimento)}${
                    idade !== null ? ` · ${idade} anos` : ''
                  }`}
                />
                <Dado
                  rotulo="CPF"
                  valor={solicitacao.pacienteCpf ? mascararCpf(solicitacao.pacienteCpf) : null}
                />
                <Dado
                  rotulo="CNS"
                  valor={solicitacao.pacienteCns ? mascararCns(solicitacao.pacienteCns) : null}
                />
                <Dado rotulo="Nome da mãe" valor={solicitacao.pacienteNomeMae} />
                <Dado
                  rotulo="Telefone"
                  valor={
                    solicitacao.pacienteTelefone
                      ? mascararTelefone(solicitacao.pacienteTelefone)
                      : null
                  }
                />
                <Dado rotulo="E-mail" valor={solicitacao.pacienteEmail} />
                <Dado rotulo="Endereço" valor={solicitacao.pacienteEndereco} larguraTotal />
              </dl>
            </CartaoConteudo>
          </Cartao>

          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo>Solicitação</CartaoTitulo>
            </CartaoCabecalho>
            <CartaoConteudo>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <Dado rotulo="Tipo" valor={ROTULOS_TIPO[solicitacao.tipo]} />
                <Dado rotulo="Especialidade" valor={solicitacao.especialidade.nome} />
                <Dado rotulo="Procedimento / exame" valor={solicitacao.procedimento} />
                <Dado
                  rotulo="Lida pela Regulação em"
                  valor={solicitacao.lidaEm ? formatarDataHora(solicitacao.lidaEm) : null}
                />
                <Dado rotulo="Regulador responsável" valor={solicitacao.regulador?.nome} />
                <Dado
                  rotulo="Documentação física recebida em"
                  valor={
                    solicitacao.documentacaoRecebidaEm
                      ? formatarDataHora(solicitacao.documentacaoRecebidaEm)
                      : null
                  }
                />
                <Dado rotulo="Observações" valor={solicitacao.observacoes} larguraTotal />
              </dl>
            </CartaoConteudo>
          </Cartao>

          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo>Documentos anexados</CartaoTitulo>
            </CartaoCabecalho>
            <CartaoConteudo>
              <PainelAnexos
                solicitacaoId={solicitacao.id}
                anexos={solicitacao.anexos}
                podeEnviar={podeAnexar}
              />
            </CartaoConteudo>
          </Cartao>
        </div>

        <div className="flex flex-col gap-5">
          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo className="flex items-center gap-2">
                <Building2 className="size-4" aria-hidden="true" />
                Unidade de origem
              </CartaoTitulo>
            </CartaoCabecalho>
            <CartaoConteudo>
              <dl className="flex flex-col gap-3">
                <Dado rotulo="UBS" valor={solicitacao.ubs.nome} />
                <Dado rotulo="E-mail institucional" valor={solicitacao.ubs.email} />
                <Dado rotulo="Telefone" valor={solicitacao.ubs.telefone} />
                <Dado rotulo="Registrada por" valor={solicitacao.criadoPor.nome} />
              </dl>
            </CartaoConteudo>
          </Cartao>

          {solicitacao.status === 'AGENDADO' && (
            <Cartao>
              <CartaoCabecalho>
                <CartaoTitulo className="flex items-center gap-2">
                  <Send className="size-4" aria-hidden="true" />
                  Avisos enviados
                </CartaoTitulo>
              </CartaoCabecalho>
              <CartaoConteudo>
                <PainelNotificacoes
                  solicitacaoId={solicitacao.id}
                  pacienteEmail={solicitacao.pacienteEmail}
                  agendada={solicitacao.status === 'AGENDADO'}
                />
              </CartaoConteudo>
            </Cartao>
          )}

          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo>Histórico</CartaoTitulo>
            </CartaoCabecalho>
            <CartaoConteudo>
              <LinhaDoTempo eventos={historico ?? []} />
            </CartaoConteudo>
          </Cartao>
        </div>
      </div>
    </div>
  );
}

function Aviso({
  icone: Icone,
  titulo,
  tom,
  children,
}: {
  icone: typeof AlertCircle;
  titulo: string;
  tom: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-lg border p-4', tom)} role="status">
      <p className="flex items-center gap-2 font-semibold">
        <Icone className="size-4.5 shrink-0" aria-hidden="true" />
        {titulo}
      </p>
      <div className="mt-1.5 text-sm">{children}</div>
    </div>
  );
}

function ItemAgendamento({ rotulo, valor }: { rotulo: string; valor?: string | null }) {
  if (!valor) return null;

  return (
    <div>
      <dt className="text-xs opacity-80">{rotulo}</dt>
      <dd className="text-sm font-medium">{valor}</dd>
    </div>
  );
}

function Dado({
  rotulo,
  valor,
  larguraTotal,
}: {
  rotulo: string;
  valor?: string | null;
  larguraTotal?: boolean;
}) {
  return (
    <div className={cn(larguraTotal && 'sm:col-span-2')}>
      <dt className="text-silenciado-texto text-xs">{rotulo}</dt>
      <dd className={cn('text-sm', !valor && 'text-silenciado-texto')}>
        {valor?.trim() ? valor : 'Não informado'}
      </dd>
    </div>
  );
}
